import { AppConfig, ProjectType, AppVariant } from '@/types/app-config';
import { determineAppVariant, getVariant, buildCustomVariant } from './app-variants';

/**
 * Configuration for the routing system
 */
export interface RoutingConfig {
  // Project configuration
  projectType: ProjectType;
  variant: AppVariant;
  
  // Feature detection
  enableFeatureDetection: boolean;
  fallbackVariant: AppVariant;
  
  // Router options
  enableLazyLoading: boolean;
  enableDataPreloading: boolean;
  enableGuardChaining: boolean;
  
  // Development options
  enableRouteDebugLog: boolean;
  enablePerformanceMetrics: boolean;
  
  // Future RBAC preparation
  rbacEnabled: boolean;
  rbacMode: 'permissive' | 'strict';
}

/**
 * Default routing configuration
 */
export const DEFAULT_ROUTING_CONFIG: RoutingConfig = {
  projectType: 'white_label' as ProjectType, // Will be determined at runtime
  variant: 'organizations',
  enableFeatureDetection: true,
  fallbackVariant: 'organizations',
  enableLazyLoading: true,
  enableDataPreloading: true,
  enableGuardChaining: true,
  enableRouteDebugLog: import.meta.env.DEV,
  enablePerformanceMetrics: import.meta.env.DEV,
  rbacEnabled: false,
  rbacMode: 'permissive'
};

/**
 * Runtime routing configuration builder
 */
export class RoutingConfigBuilder {
  private config: RoutingConfig;
  
  constructor(baseConfig: Partial<RoutingConfig> = {}) {
    this.config = { ...DEFAULT_ROUTING_CONFIG, ...baseConfig };
  }
  
  /**
   * Set project type (white_label or private_label)
   */
  setProjectType(type: ProjectType): this {
    this.config.projectType = type;
    return this;
  }
  
  /**
   * Set specific variant
   */
  setVariant(variant: AppVariant): this {
    this.config.variant = variant;
    return this;
  }
  
  /**
   * Enable feature detection to auto-determine variant
   */
  enableFeatureDetection(enabled: boolean = true): this {
    this.config.enableFeatureDetection = enabled;
    return this;
  }
  
  /**
   * Set development options
   */
  setDevelopmentMode(enabled: boolean = true): this {
    this.config.enableRouteDebugLog = enabled;
    this.config.enablePerformanceMetrics = enabled;
    return this;
  }
  
  /**
   * Enable RBAC (for future use)
   */
  enableRBAC(enabled: boolean = true, mode: 'permissive' | 'strict' = 'permissive'): this {
    this.config.rbacEnabled = enabled;
    this.config.rbacMode = mode;
    return this;
  }
  
  /**
   * Build the final configuration
   */
  build(): RoutingConfig {
    return { ...this.config };
  }
}

/**
 * Builds app configuration from routing config and features
 */
export function buildAppConfig(
  routingConfig: RoutingConfig,
  features?: Record<string, boolean>
): AppConfig {
  let variant = routingConfig.variant;
  
  // Auto-detect variant from features if enabled
  if (routingConfig.enableFeatureDetection && features) {
    const detectedVariant = determineAppVariant(features);
    variant = detectedVariant;
    
    if (routingConfig.enableRouteDebugLog) {
      console.log(`[Routing] Auto-detected variant: ${variant}`, { features });
    }
  }
  
  // Get variant configuration
  let variantConfig;
  try {
    if (variant === 'custom' && features) {
      variantConfig = buildCustomVariant(features);
    } else {
      variantConfig = getVariant(variant);
    }
  } catch (error) {
    console.warn(`[Routing] Failed to load variant ${variant}, falling back to ${routingConfig.fallbackVariant}`);
    variantConfig = getVariant(routingConfig.fallbackVariant);
    variant = routingConfig.fallbackVariant;
  }
  
  // Build the final app configuration
  const appConfig: AppConfig = {
    projectType: routingConfig.projectType,
    variant,
    appModules: variantConfig.appModules,
    adminModules: variantConfig.adminModules,
    routing: {
      basePath: routingConfig.projectType === 'white_label' ? '/:projectId' : '/',
      projectIdSegment: routingConfig.projectType === 'white_label' ? '/:projectId' : '',
      defaultRoute: variantConfig.defaultRoute || 'directory',
      errorRoutes: {
        notFound: '404',
        forbidden: '403',
        unauthorized: '401',
        featureNotEnabled: 'feature-not-enabled'
      }
    },
    // Future RBAC configuration
    rbac: {
      enabled: routingConfig.rbacEnabled,
      defaultRole: 'user',
      roleHierarchy: ['user', 'admin', 'super_admin']
    }
  };
  
  if (routingConfig.enableRouteDebugLog) {
    console.log('[Routing] Built app configuration:', appConfig);
  }
  
  return appConfig;
}

/**
 * Creates a routing configuration for different environments
 */
export function createRoutingConfig(options: {
  projectType?: ProjectType;
  variant?: AppVariant;
  isDevelopment?: boolean;
  enableRBAC?: boolean;
}): RoutingConfig {
  const builder = new RoutingConfigBuilder();
  
  if (options.projectType) {
    builder.setProjectType(options.projectType);
  }
  
  if (options.variant) {
    builder.setVariant(options.variant);
  }
  
  if (options.isDevelopment !== undefined) {
    builder.setDevelopmentMode(options.isDevelopment);
  }
  
  if (options.enableRBAC) {
    builder.enableRBAC(true, 'permissive');
  }
  
  return builder.build();
}

/**
 * Validates routing configuration
 */
export function validateRoutingConfig(config: RoutingConfig): string[] {
  const errors: string[] = [];
  
  if (!config.projectType) {
    errors.push('Project type is required');
  }
  
  if (!config.variant) {
    errors.push('Variant is required');
  }
  
  if (!config.fallbackVariant) {
    errors.push('Fallback variant is required');
  }
  
  // Validate that fallback variant exists
  try {
    getVariant(config.fallbackVariant);
  } catch (error) {
    errors.push(`Invalid fallback variant: ${config.fallbackVariant}`);
  }
  
  return errors;
} 